import * as ENGINE from '@gnsx/genesys.js';
import { runInAction } from 'mobx';

import type { OutlinerNodeData } from '@/app/editor/components/tree/shared/types/tree.types';
import type { ISceneTreeNode } from '@/editor/store/models/sceneTree';
import type { TreeInstance } from '@headless-tree/core';

import { actorParenting, nameHelpers } from '@/editor/core';
import { getSceneUndoRedo } from '@/editor/core/undoRedo';
import { editorStore, sceneStore } from '@/editor/store';
import { SceneTreeNode } from '@/editor/store/models/sceneTree';
import { adjustMoveInsertIndex } from '@/editor/store/models/sceneTreeGuards';

export function addActor(className: string) {
  editorStore.scene.addObject(className);
}

export function addFolder(tree: TreeInstance<OutlinerNodeData>) {
  const selectedNodeIds = tree.getSelectedItems()
    .map(item => item.getItemData()?.id)
    .filter((id): id is string => Boolean(id));
  const folderNodeId = createFolder(undefined, selectedNodeIds.length > 0 ? selectedNodeIds : undefined);
  if (folderNodeId) {
    tree.getItemInstance(folderNodeId)?.startRenaming();
  }
}

/**
 * Create a folder in the outliner tree with full undo/redo support.
 *    - No selection: Creates an empty folder at the root level.
 *    - Single folder selected: Creates a new folder as a child of the selected folder and expands the parent.
 *    - Scene selected: Creates a folder at the root level.
 *    - Other items selected: Creates a folder containing the selected items, placed at their original position.
 *
 * Returns the new folder's node ID so the caller can trigger renaming.
 */
export function createFolder(name?: string, selectedItemIds?: string[]): string | undefined {
  const selectedTopLevelNodes = sceneStore.outliner.getTopLevelNodes(selectedItemIds ?? []);
  const firstSelectedNode = selectedTopLevelNodes[0];

  // --- Compute all placement parameters upfront (no mutations yet) ---

  let targetParentId: string = sceneStore.outliner.rootNode?.id ?? '';
  let folderIndex: number | null = null; // null = append at end
  let wrapNodeIds: string[] = [];
  let expandParentId: string | null = null;

  if (selectedTopLevelNodes.length === 0 || firstSelectedNode?.isScene) {
    // Empty folder at root, appended at the end.
  } else if (selectedTopLevelNodes.length === 1 && firstSelectedNode?.isFolder) {
    // New folder as child of the selected folder.
    targetParentId = firstSelectedNode.id;
    expandParentId = firstSelectedNode.id;
  } else {
    const targetParentNode = firstSelectedNode
      ? sceneStore.outliner.findClosestFolderOrSceneParent(firstSelectedNode)
      : (sceneStore.outliner.rootNode ?? null);
    if (!targetParentNode) return undefined;
    targetParentId = targetParentNode.id;

    // Only wrap nodes that are direct children of targetParent.
    wrapNodeIds = selectedTopLevelNodes
      .filter(node => {
        const parent = sceneStore.outliner.findParentNode(node.id);
        return parent?.id === targetParentId && (node.nodeType === 'actor' || node.nodeType === 'folder');
      })
      .map(node => node.id);

    // The folder lands at the index of the first wrapped node in the parent.
    if (wrapNodeIds.length > 0) {
      folderIndex = targetParentNode.children.findIndex(c => c.id === wrapNodeIds[0]);
    }
  }

  if (!targetParentId) return undefined;

  // Stable ID and name generated upfront so redo recreates the exact same node.
  const folderId = `folder-${ENGINE.shortUUID()}`;
  const folderName = name ?? sceneStore.outliner.generateUniqueFolderName();

  const undoRedo = getSceneUndoRedo();
  undoRedo.beginGroup('Create Folder');

  void undoRedo.recordAction(
    'Create Folder Node',
    () => {
      runInAction(() => {
        const targetParent = sceneStore.outliner.getNodeById(targetParentId);
        if (!targetParent) return;
        sceneStore.outliner.addNode(
          SceneTreeNode.create({ id: folderId, name: folderName, nodeType: 'folder', isRoot: false, isExpanded: true, isSelected: true }),
          targetParent,
        );
        if (folderIndex !== null) {
          sceneStore.outliner.setChildIndex(folderId, targetParentId, folderIndex);
        }
        if (expandParentId) {
          sceneStore.outliner.setExpandedNodeIds(prev =>
            prev.includes(expandParentId!) ? prev : [...prev, expandParentId!]
          );
        }
        sceneStore.outliner.selectNode(folderId);
        sceneStore.updateOutliner();
      });
    },
    () => {
      runInAction(() => {
        sceneStore.outliner.removeNode(folderId);
        sceneStore.updateOutliner();
      });
    },
  );

  // Move each selected node into the folder within the same group.
  if (wrapNodeIds.length > 0) {
    moveOutlinerNodes({ type: 'reparent', nodeIds: wrapNodeIds, targetParentId: folderId });
  }

  undoRedo.endGroup();

  return folderId;
}

/**
 * Deletes a folder along with every descendant actor and nested folder as a single
 * undoable group. Use this for the destructive "Delete Folder" action.
 */
export function deleteFolderAndContents(folderId: string) {
  const folder = sceneStore.outliner.getNodeById(folderId);
  if (!folder?.isFolder) return;

  const parentNode = sceneStore.outliner.findParentNode(folderId);
  if (!parentNode) return;

  const folderName = folder.name;
  const folderIsExpanded = folder.isExpanded;
  const folderPosition = sceneStore.outliner.getChildIndex(folderId);
  if (!folderPosition) return;

  // Collect descendants. Nested folders are recorded deepest-first so removal happens
  // bottom-up; on undo the reverse order restores parents before their children.
  interface NestedFolderSnapshot {
    id: string;
    parentId: string;
    index: number;
    name: string;
    isExpanded: boolean;
  }
  const actorsToDelete: ENGINE.Actor[] = [];
  const nestedFolderSnapshots: NestedFolderSnapshot[] = [];

  const collectDescendants = (node: ISceneTreeNode) => {
    for (const child of node.children) {
      if (child.nodeType === 'actor') {
        if (child.asActor) actorsToDelete.push(child.asActor);
      } else if (child.nodeType === 'folder') {
        collectDescendants(child);
        const position = sceneStore.outliner.getChildIndex(child.id);
        if (position) {
          nestedFolderSnapshots.push({
            id: child.id,
            parentId: position.parentId,
            index: position.index,
            name: child.name,
            isExpanded: child.isExpanded,
          });
        }
      }
    }
  };
  collectDescendants(folder);

  // `removeObjects` undo re-adds actors via `updateOutliner` → `addActorsToOutliner`, which
  // always parents actor nodes under the scene root. Capture true outliner parents first,
  // then register an action committed *before* `Remove Objects` so its undo runs *after*
  // actors are restored and can reparent them back under folders.
  const actorOutlinerPlacements: Array<{ actorUuid: string; parentId: string; index: number }> = [];
  for (const actor of actorsToDelete) {
    const position = sceneStore.outliner.getChildIndex(actor.uuid);
    if (position) {
      actorOutlinerPlacements.push({
        actorUuid: actor.uuid,
        parentId: position.parentId,
        index: position.index,
      });
    }
  }

  const undoRedo = getSceneUndoRedo();
  undoRedo.beginGroup('Delete Folder');

  if (actorOutlinerPlacements.length > 0) {
    void undoRedo.recordAction(
      'Restore Deleted Folder Actors To Tree',
      () => {
        // Forward delete and redo are handled by `Remove Objects` and folder removal actions.
      },
      () => {
        runInAction(() => {
          const byParent = new Map<string, typeof actorOutlinerPlacements>();
          for (const placement of actorOutlinerPlacements) {
            const list = byParent.get(placement.parentId) ?? [];
            list.push(placement);
            byParent.set(placement.parentId, list);
          }
          for (const list of byParent.values()) {
            list.sort((a, b) => a.index - b.index);
          }
          for (const list of byParent.values()) {
            for (const { actorUuid, parentId, index } of list) {
              const parent = sceneStore.outliner.getNodeById(parentId);
              if (!parent) continue;
              sceneStore.outliner.setChildIndex(actorUuid, parentId, index);
            }
          }
          sceneStore.updateOutliner();
          const world = sceneStore.world;
          if (world) {
            const result = actorParenting.rebuildParentingFromTree(sceneStore.outliner.rootNode, world);
            if (result.errors.length > 0) {
              console.warn('[outlinerActions] rebuildParentingFromTree completed with recoverable errors', result.errors);
            }
          }
        });
      },
    );
  }

  if (actorsToDelete.length > 0) {
    sceneStore.removeObjects(...actorsToDelete);
  }

  for (const snapshot of nestedFolderSnapshots) {
    void undoRedo.recordAction(
      'Delete Nested Folder Node',
      () => {
        runInAction(() => {
          sceneStore.outliner.removeNode(snapshot.id);
          sceneStore.updateOutliner();
        });
      },
      () => {
        runInAction(() => {
          const parent = sceneStore.outliner.getNodeById(snapshot.parentId);
          if (!parent) return;
          sceneStore.outliner.addNode(
            SceneTreeNode.create({
              id: snapshot.id,
              name: snapshot.name,
              nodeType: 'folder',
              isRoot: false,
              isExpanded: snapshot.isExpanded,
              isSelected: false,
            }),
            parent,
          );
          sceneStore.outliner.setChildIndex(snapshot.id, snapshot.parentId, snapshot.index);
          sceneStore.updateOutliner();
        });
      },
    );
  }

  void undoRedo.recordAction(
    'Delete Folder Node',
    () => {
      runInAction(() => {
        sceneStore.outliner.removeNode(folderId);
        sceneStore.updateOutliner();
      });
    },
    () => {
      runInAction(() => {
        const parent = sceneStore.outliner.getNodeById(parentNode.id);
        if (!parent) return;
        sceneStore.outliner.addNode(
          SceneTreeNode.create({
            id: folderId,
            name: folderName,
            nodeType: 'folder',
            isRoot: false,
            isExpanded: folderIsExpanded,
            isSelected: false,
          }),
          parent,
        );
        sceneStore.outliner.setChildIndex(folderId, parentNode.id, folderPosition.index);
        sceneStore.updateOutliner();
      });
    },
  );

  undoRedo.endGroup();
}

/**
 * Deletes a folder, moving its direct children up to the folder's parent (ungroup).
 * This is the symmetric inverse of createFolder.
 */
export function deleteFolderKeepContents(folderId: string) {
  const folder = sceneStore.outliner.getNodeById(folderId);
  if (!folder?.isFolder) return;

  const parentNode = sceneStore.outliner.findParentNode(folderId);
  if (!parentNode) return;

  // Capture folder metadata and position upfront before any mutation.
  const folderName = folder.name;
  const folderPosition = sceneStore.outliner.getChildIndex(folderId);
  if (!folderPosition) return;

  // Capture child IDs before any mutation.
  const childIds = folder.children.map(child => child.id);

  const undoRedo = getSceneUndoRedo();
  undoRedo.beginGroup('Delete Folder Only');

  // Move each child out to the parent, stacking them at the folder's position.
  for (const nodeId of childIds) {
    moveOutlinerNode(nodeId, parentNode.id, folderPosition.index);
  }

  // Remove the now-empty folder node.
  void undoRedo.recordAction(
    'Delete Folder Node',
    () => {
      runInAction(() => {
        sceneStore.outliner.removeNode(folderId);
        sceneStore.updateOutliner();
      });
    },
    () => {
      runInAction(() => {
        const parent = sceneStore.outliner.getNodeById(parentNode.id);
        if (!parent) return;
        sceneStore.outliner.addNode(
          SceneTreeNode.create({ id: folderId, name: folderName, nodeType: 'folder', isRoot: false, isExpanded: true, isSelected: false }),
          parent,
        );
        sceneStore.outliner.setChildIndex(folderId, parentNode.id, folderPosition.index);
        sceneStore.updateOutliner();
      });
    },
  );

  undoRedo.endGroup();
}

/**
 * Rebuilds actor engine parenting from the outliner tree and refreshes the outliner UI.
 * Must be called after any structural change to the tree.
 */
function syncAfterMove() {
  sceneStore.updateOutliner();
  const world = sceneStore.world;
  if (world) {
    const result = actorParenting.rebuildParentingFromTree(sceneStore.outliner.rootNode, world);
    if (result.errors.length > 0) {
      console.warn('[outlinerActions] rebuildParentingFromTree completed with recoverable errors', result.errors);
    }
  }
}

export type OutlinerMoveOperation =
  | { type: 'reparent'; nodeIds: string[]; targetParentId: string | null }
  | { type: 'before'; nodeIds: string[]; targetId: string }
  | { type: 'after'; nodeIds: string[]; targetId: string };

/**
 * Moves multiple outliner nodes as a single undoable step, built on `moveOutlinerNode`.
 *
 * When called inside an open group (beginGroup/endGroup), the moves are folded into
 * that group. When called standalone, each node move becomes its own group.
 *
 * Returns the IDs of nodes that were successfully moved.
 */
export function moveOutlinerNodes(operation: OutlinerMoveOperation): string[] {
  const { nodeIds } = operation;
  if (nodeIds.length === 0) return [];

  const undoRedo = getSceneUndoRedo();
  const isInsideGroup = undoRedo.isGroupOpen;
  if (!isInsideGroup) {
    undoRedo.beginGroup('Move Nodes');
  }

  const movedIds: string[] = [];

  if (operation.type === 'reparent') {
    for (const nodeId of nodeIds) {
      const moved = moveOutlinerNode(nodeId, operation.targetParentId, null);
      if (moved) movedIds.push(nodeId);
    }
  } else {
    const anchor = sceneStore.outliner.getNodeById(operation.targetId);
    const anchorParent = anchor ? sceneStore.outliner.findParentNode(anchor.id) : null;
    if (!anchor || !anchorParent) {
      if (!isInsideGroup) undoRedo.endGroup();
      return [];
    }

    const anchorIndex = anchorParent.children.findIndex(c => c.id === anchor.id);
    if (anchorIndex === -1) {
      if (!isInsideGroup) undoRedo.endGroup();
      return [];
    }

    let insertPosition = operation.type === 'before' ? anchorIndex : anchorIndex + 1;
    const sortedNodeIds = [...nodeIds].sort((a, b) => {
      const indexA = anchorParent.children.findIndex(c => c.id === a);
      const indexB = anchorParent.children.findIndex(c => c.id === b);
      const sortA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
      const sortB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
      return sortA - sortB;
    });

    sortedNodeIds.forEach((nodeId, offset) => {
      const currentIndex = anchorParent.children.findIndex(c => c.id === nodeId);
      const desiredPosition = insertPosition + offset;
      const adjustedIndex = currentIndex === -1
        ? desiredPosition
        : adjustMoveInsertIndex(currentIndex, desiredPosition);

      const moved = moveOutlinerNode(nodeId, anchorParent.id, adjustedIndex);
      if (moved) {
        movedIds.push(nodeId);
        if (currentIndex !== -1 && currentIndex < insertPosition) {
          insertPosition--;
        }
      }
    });
  }

  if (!isInsideGroup) {
    undoRedo.endGroup();
  }

  return movedIds;
}

/**
 * Primitive: moves a single outliner node to a new parent and position with undo/redo support.
 *
 * @param nodeId - The node to move.
 * @param newParentId - The target parent node ID, or `null` to move to the root.
 * @param index - The child index within the new parent, or `null` to append at the end.
 *
 * When called inside an open group, the action is folded into that group.
 * When called standalone, it forms its own single-action group.
 *
 * Returns `true` if the node was successfully moved.
 */
export function moveOutlinerNode(
  nodeId: string,
  newParentId: string | null,
  index: number | null,
): boolean {
  const resolvedParentId = newParentId ?? sceneStore.outliner.rootNode?.id;
  if (!resolvedParentId) return false;

  const before = sceneStore.outliner.getChildIndex(nodeId);
  if (!before) return false;

  const applyMove = () => {
    runInAction(() => {
      const targetParent = sceneStore.outliner.getNodeById(resolvedParentId);
      if (!targetParent) return;
      const targetIndex = index ?? targetParent.children.length;
      sceneStore.outliner.setChildIndex(nodeId, resolvedParentId, targetIndex);
    });
    syncAfterMove();
  };

  const applyUndo = () => {
    runInAction(() => {
      sceneStore.outliner.setChildIndex(nodeId, before.parentId, before.index);
    });
    syncAfterMove();
  };

  const undoRedo = getSceneUndoRedo();
  const isInsideGroup = undoRedo.isGroupOpen;
  if (!isInsideGroup) {
    undoRedo.beginGroup('Move Node');
  }

  void undoRedo.recordAction('Move Node', applyMove, applyUndo);

  if (!isInsideGroup) {
    undoRedo.endGroup();
  }

  return true;
}

export function renameSelectedNodes(newName: string) {
  const selectedNodes = sceneStore.outliner.selectedNodeIds
    .map(id => sceneStore.outliner.getNodeById(id))
    .filter((node): node is ISceneTreeNode => node !== undefined && (node.isActorNode || node.isFolder));
  if (!selectedNodes.length) return;

  // Collect all existing names from the outliner tree.
  const existingNames: string[] = [];
  const collectAllNames = (node: ISceneTreeNode) => {
    if (node.name && (node.isActorNode || node.isFolder)) {
      existingNames.push(node.name);
    }
    node.children.forEach(collectAllNames);
  };

  const rootNode = sceneStore.outliner.rootNode;
  if (rootNode) {
    collectAllNames(rootNode);
  }

  // Pre-compute new unique names and capture old names before any mutation.
  // Unique names must be computed upfront so redo applies the same names as the
  // original action (not re-derived against a potentially different tree state).
  const renames: Array<{ nodeId: string; oldName: string; newName: string }> = [];
  for (const node of selectedNodes) {
    const uniqueName = nameHelpers.makeUniqueName(newName, existingNames);
    existingNames.push(uniqueName); // Reserve so subsequent nodes in this batch don't collide.
    renames.push({ nodeId: node.id, oldName: node.name ?? '', newName: uniqueName });
  }

  const applyRenames = (entries: typeof renames) => {
    runInAction(() => {
      for (const { nodeId, newName: nameToApply } of entries) {
        const node = sceneStore.outliner.getNodeById(nodeId);
        if (!node) continue;

        if (node.isActorNode) {
          const selectedEditable = sceneStore.getSelectedObjectById(nodeId);
          if (selectedEditable) {
            selectedEditable.setName(nameToApply);
          } else if (node.asActor) {
            // Fallback for non-selected actor-node renames triggered by tree state drift.
            nameHelpers.setCustomDisplayName(node.asActor, nameToApply);
          }
          node.setName(nameToApply);
        } else if (node.isFolder) {
          node.setName(nameToApply);
        }

        sceneStore.updateOutliner();
      }
    });
  };

  const doRenames = renames.map(r => ({ nodeId: r.nodeId, oldName: r.oldName, newName: r.newName }));
  const undoRenames = renames.map(r => ({ nodeId: r.nodeId, oldName: r.newName, newName: r.oldName }));

  const undoRedo = getSceneUndoRedo();
  void undoRedo.recordAction(
    'Rename Node',
    () => { applyRenames(doRenames); },
    () => { applyRenames(undoRenames); },
  );
}
